import {
  FAQItem,
  TestimonialItem,
  BlogPost,
  CreateFAQRequest,
  UpdateFAQRequest,
  CreateTestimonialRequest,
  UpdateTestimonialRequest,
  CreateBlogPostRequest,
  UpdateBlogPostRequest,
  LoginRequest,
  LoginApiResponse,
  AuthResponse,
  ApiResponse,
  SiteSettings
} from '../types/api';

// Configuração da API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://luararezende.adv.br/api';

// Cliente HTTP genérico
class ApiClient {
  private baseUrl: string;
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    useJWT: boolean = false
  ): Promise<ApiResponse<T>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Adicionar headers customizados se existirem
      if (options.headers) {
        const customHeaders = options.headers as Record<string, string>;
        Object.keys(customHeaders).forEach(key => {
          headers[key] = customHeaders[key];
        });
      }

      // Para endpoints admin, usar JWT
      if (useJWT) {
        const token = sessionStorage.getItem('adminToken');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else {
          return {
            success: false,
            error: 'Token de autenticação não encontrado',
          };
        }
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      // Verificar se a resposta é JSON válido
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('Erro ao fazer parse do JSON:', jsonError);
          return {
            success: false,
            error: 'Resposta da API não é um JSON válido',
          };
        }
      } else {
        // Se não for JSON, tentar ler como texto
        const textData = await response.text();
        console.error('Resposta não é JSON:', textData);
        return {
          success: false,
          error: 'Resposta da API não é um JSON válido',
        };
      }

      if (!response.ok) {
        return {
          success: false,
          error: data.message || `Erro ${response.status}`,
        };
      }

      return {
        success: true,
        data: data.data || data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro de rede',
      };
    }
  }

  // Métodos para endpoints públicos (home)
  async getPublic<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' }, false);
  }

  // Métodos para endpoints admin (com JWT)
  async getAdmin<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' }, true);
  }

  async postAdmin<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }, true);
  }

  // multipart/form-data for file uploads (JWT)
  async postAdminForm<T>(endpoint: string, form: FormData): Promise<ApiResponse<T>> {
    try {
      const headers: Record<string, string> = {};
      // attach JWT
      const token = sessionStorage.getItem('adminToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers, // do not set Content-Type manually
        body: form,
      });
      
      // Verificar se a resposta é JSON válido
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('Erro ao fazer parse do JSON no upload:', jsonError);
          return { success: false, error: 'Resposta da API não é um JSON válido' };
        }
      } else {
        // Se não for JSON, tentar ler como texto
        const textData = await response.text();
        console.error('Resposta não é JSON no upload:', textData);
        return { success: false, error: 'Resposta da API não é um JSON válido' };
      }
      
      if (!response.ok) {
        return { success: false, error: data.message || `Erro ${response.status}` };
      }
      return { success: true, data: data.data || data } as ApiResponse<T>;
    } catch (error: any) {
      return { success: false, error: error.message || 'Erro de rede' } as ApiResponse<T>;
    }
  }

  // Alias para postAdminForm para consistência
  async postAdminFormData<T>(endpoint: string, form: FormData): Promise<ApiResponse<T>> {
    return this.postAdminForm<T>(endpoint, form);
  }

  async putAdmin<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, true);
  }

  // multipart/form-data for file uploads (JWT) - PUT method
  async putAdminForm<T>(endpoint: string, form: FormData): Promise<ApiResponse<T>> {
    try {
      const headers: Record<string, string> = {};
      // attach JWT
      const token = sessionStorage.getItem('adminToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers, // do not set Content-Type manually
        body: form,
      });
      
      // Verificar se a resposta é JSON válido
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('Erro ao fazer parse do JSON no upload:', jsonError);
          return { success: false, error: 'Resposta da API não é um JSON válido' };
        }
      } else {
        // Se não for JSON, tentar ler como texto
        const textData = await response.text();
        console.error('Resposta não é JSON no upload:', textData);
        return { success: false, error: 'Resposta da API não é um JSON válido' };
      }
      
      if (!response.ok) {
        return { success: false, error: data.message || `Erro ${response.status}` };
      }
      return { success: true, data: data.data || data } as ApiResponse<T>;
    } catch (error: any) {
      return { success: false, error: error.message || 'Erro de rede' } as ApiResponse<T>;
    }
  }

  async deleteAdmin<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: JSON.stringify(data),
    }, true);
  }

  // Métodos para autenticação
  async postAuth<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }, false);
  }

  // Método específico para login - retorna a resposta direta da API
  async postLogin<T>(endpoint: string, data: any): Promise<T> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Removido Basic Auth exposto

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });

      // Verificar se a resposta é JSON válido
      let result;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          result = await response.json();
        } catch (jsonError) {
          console.error('Erro ao fazer parse do JSON no login:', jsonError);
          throw new Error('Resposta da API não é um JSON válido');
        }
      } else {
        // Se não for JSON, tentar ler como texto
        const textData = await response.text();
        console.error('Resposta não é JSON no login:', textData);
        throw new Error('Resposta da API não é um JSON válido');
      }

      if (!response.ok) {
        throw new Error(result.message || `Erro ${response.status}`);
      }

      return result as T;
    } catch (error: any) {
      throw new Error(error.message || 'Erro de rede');
    }
  }
}

const apiClient = new ApiClient(API_BASE_URL);

// Serviços para FAQ
export const faqService = {
  async getAll(): Promise<ApiResponse<FAQItem[]>> {
    return apiClient.getPublic<FAQItem[]>('/faq');
  },
  async getById(id: number): Promise<ApiResponse<FAQItem>> {
    return apiClient.getPublic<FAQItem>(`/faq/${id}`);
  },
  async create(data: CreateFAQRequest): Promise<ApiResponse<FAQItem>> {
    return apiClient.postAdmin<FAQItem>('/faq', data);
  },
  async update(id: number, data: UpdateFAQRequest): Promise<ApiResponse<FAQItem>> {
    return apiClient.putAdmin<FAQItem>('/faq', { id, ...data });
  },
  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.deleteAdmin<void>('/faq', { id });
  },
};

// Serviços para Testimonials
export const testimonialService = {
  async getAll(): Promise<ApiResponse<TestimonialItem[]>> {
    return apiClient.getPublic<TestimonialItem[]>('/testimonials');
  },
  async getById(id: number): Promise<ApiResponse<TestimonialItem>> {
    return apiClient.getPublic<TestimonialItem>(`/testimonials/${id}`);
  },
  async create(data: CreateTestimonialRequest): Promise<ApiResponse<TestimonialItem>> {
    return apiClient.postAdmin<TestimonialItem>('/testimonials', data);
  },
  async update(id: number, data: UpdateTestimonialRequest): Promise<ApiResponse<TestimonialItem>> {
    return apiClient.putAdmin<TestimonialItem>('/testimonials', { id, ...data });
  },
  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.deleteAdmin<void>('/testimonials', { id });
  },
};

// Serviços para Blog
export const blogService = {
  async getAll(page: number = 1, limit: number = 10): Promise<ApiResponse<BlogPost[]>> {
    return apiClient.getPublic<BlogPost[]>(`/blog?page=${page}&limit=${limit}`);
  },
  async getById(id: number): Promise<ApiResponse<BlogPost>> {
    return apiClient.getPublic<BlogPost>(`/blog/${id}`);
  },
  async getBySlug(slug: string): Promise<ApiResponse<BlogPost>> {
    return apiClient.getPublic<BlogPost>(`/blog?slug=${encodeURIComponent(slug)}`);
  },
  async getFeatured(): Promise<ApiResponse<BlogPost[]>> {
    return apiClient.getPublic<BlogPost[]>('/blog/featured');
  },
  async getByCategory(category: string): Promise<ApiResponse<BlogPost[]>> {
    return apiClient.getPublic<BlogPost[]>(`/blog/category/${category}`);
  },
  async create(data: CreateBlogPostRequest, file?: File): Promise<ApiResponse<BlogPost>> {
    if (file) {
      // Criar FormData para envio de arquivo
      const formData = new FormData();
      
      // Adicionar todos os campos do post
      Object.keys(data).forEach(key => {
        const value = data[key as keyof CreateBlogPostRequest];
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });
      
      // Adicionar o arquivo
      formData.append('file', file);
      
      return apiClient.postAdminFormData<BlogPost>('/blog', formData);
    } else {
      return apiClient.postAdmin<BlogPost>('/blog', data);
    }
  },
  async update(id: number, data: UpdateBlogPostRequest, file?: File): Promise<ApiResponse<BlogPost>> {
    if (file) {
      // Criar FormData para envio de arquivo
      const formData = new FormData();
      
      // Adicionar o ID primeiro
      formData.append('id', id.toString());
      
      // Adicionar todos os campos do post (excluindo 'id' se existir, pois já foi adicionado)
      Object.keys(data).forEach(key => {
        // Pular o campo 'id' se existir, pois já foi adicionado manualmente
        if (key === 'id') return;
        
        const value = data[key as keyof UpdateBlogPostRequest];
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });
      
      // Adicionar o arquivo
      formData.append('file', file);
      
      return apiClient.putAdminForm<BlogPost>('/blog', formData);
    } else {
      // Remover 'id' do data se existir, pois será passado separadamente
      const { id: _, ...dataWithoutId } = data as any;
      return apiClient.putAdmin<BlogPost>('/blog', { id, ...dataWithoutId });
    }
  },
  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.deleteAdmin<void>('/blog', { id });
  },
  async uploadImage(file: File): Promise<ApiResponse<{ url: string }>> {
    const form = new FormData();
    form.append('file', file);
    return apiClient.postAdminForm<{ url: string }>(`/blog/upload`, form);
  },
};

// Serviços de autenticação
export const authService = {
  async login(credentials: LoginRequest): Promise<LoginApiResponse> {
    return apiClient.postLogin<LoginApiResponse>('/auth/login', credentials);
  },
  async logout(): Promise<ApiResponse<void>> {
    return apiClient.postAuth<void>('/auth/logout', {});
  },
  async getCurrentUser(): Promise<ApiResponse<AuthResponse>> {
    return apiClient.getAdmin<AuthResponse>('/auth/me');
  },
};

// Serviços para Configurações do Site
export const settingsService = {
  // Público (Basic Auth)
  async getPublic(): Promise<ApiResponse<SiteSettings>> {
    return apiClient.getPublic<SiteSettings>('/settings');
  },
  // Admin (JWT)
  async update(partial: Partial<SiteSettings>): Promise<ApiResponse<SiteSettings>> {
    return apiClient.putAdmin<SiteSettings>('/settings', partial);
  },
};

// Serviços para Perfil do Usuário
export const profileService = {
  async getCurrentUser(): Promise<ApiResponse<any>> {
    return apiClient.getAdmin<any>('/profile');
  },
  async updateProfile(data: { 
    name: string; 
    email: string; 
    password?: string; 
  }): Promise<ApiResponse<any>> {
    return apiClient.putAdmin<any>('/profile', data);
  },
};

// Serviços para WhatsApp
export const whatsappService = {
  async getStatus(): Promise<ApiResponse<any>> {
    // Agora consome nossa própria API que funciona como proxy seguro para o n8n
    return fetch(`${API_BASE_URL}/whatsapp/status`)
      .then(res => res.json())
      .then(data => ({ success: true, data }));
  },
};